import cx from 'classnames'
import {InputProvider} from 'components/common/CartItemBuilder/providers/InputProvider'
import {VentrataDataProvider} from 'components/common/CartItemBuilder/providers/VentrataDataProvider'
import {ErrorMessage} from 'components/common/ErrorMessage'
import {Warning} from 'components/system/Warning'
import {DATE_FORMAT} from 'consts'
import {map, max, range, values, zip} from 'lodash'
import moment from 'moment'
import {EnvironmentProvider} from 'providers/EnvironmentProvider'
import {useCallback, useMemo} from 'react'
import {Ventrata} from 'types'
import {ga4} from 'utils/ga4'
import {StepDataProvider} from '../../../../providers/StepDataProvider/StepDataProvider'
import {MonthSelector} from './MonthSelector'

const DAYS_OF_WEEK = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

export const MaximizedCalendar = () => {
  const dataManager = StepDataProvider.useDateDataManager()
  const product = VentrataDataProvider.useProduct('optional')
  const totalUnitsCounter = StepDataProvider.guests.useTotalUnitsCounter()
  const ampleOptions = VentrataDataProvider.useAmpleOptions(totalUnitsCounter, 'optional')
  const optionalOptionId = EnvironmentProvider.useOptionalOptionId()
  const month = InputProvider.useMonth()
  const {setMonth} = InputProvider.useMutators()
  const firstMonthDay = moment(month).day()
  const error = VentrataDataProvider.useError()
  const calendarData = VentrataDataProvider.useCalendar('optional')

  const calendar = useMemo(() => {
    return calendarData ? (zip(...values(calendarData)) as Ventrata.CalendarDay[][]) : []
  }, [calendarData])

  const optionIds = useMemo(
    () => (optionalOptionId ? [optionalOptionId] : map(ampleOptions, 'id')),
    [optionalOptionId, ampleOptions]
  )

  const onSelectDate = useCallback(
    (calendarDate: string) => {
      dataManager.save(calendarDate)

      ga4({
        event: 'select_date',
        label: calendarDate,
        item_id: product.id,
        item_name: product.title,
        checkout_type: 'custom_checkout',
      })
    },
    [product, dataManager]
  )

  if (error) {
    return <ErrorMessage error={error} />
  }

  if (!calendar) {
    return null
  }

  return (
    <div className="flex flex-col">
      <MonthSelector month={month} setMonth={setMonth} />
      <div className="grid grid-cols-7 justify-items-center border-solid border-b border-b-gray-20 pt-2">
        {DAYS_OF_WEEK.map((day, i) => {
          return (
            <div key={i} className="text-gray-0 font-bold py-3">
              {day}
            </div>
          )
        })}
      </div>
      <div className="grid grid-cols-7 justify-items-center">
        {range(firstMonthDay).map((i) => {
          return <div key={i} />
        })}

        {range(0, moment(month).daysInMonth()).map((dayIndex) => {
          const daysCombined = calendar[dayIndex] || []
          const calendarDateMoment = moment(month).add(dayIndex, 'days')
          const calendarDate = calendarDateMoment.format(DATE_FORMAT)
          const available = daysCombined.some((calendarDay) => calendarDay.available)

          return (
            <div
              key={calendarDate}
              className={cx('flex flex-col items-center w-full py-3 select-none text-lg', {
                'text-red-500 pointer-events-none line-through': !available,
                'cursor-pointer': available,
                'bg-theme rounded-lg text-white': dataManager.getData() === calendarDate,
              })}
              onClick={() => onSelectDate(calendarDate)}
            >
              <div>{calendarDateMoment.date()}</div>
            </div>
          )
        })}
      </div>
      {optionIds.length === 0 && (
        <Warning>
          Some dates are not available because you've exceeded the maximum number of guests for this tour (
          {max(map(product.options, 'restrictions.maxUnits'))})
        </Warning>
      )}
    </div>
  )
}
